import { FC } from "react";
import { useTranslation } from "react-i18next";

import { scanCTAStyle, uxRouteQuestionOnly } from "../../../../configs";
import {
  capitalizeFirstLetter,
  getLangBase,
  isEmptyCTAStyle,
  useIsMobile,
} from "../../../../utils";
import { findBrandByDomain } from "../../../../configs/configLoader";
import { LANGUAGE_RULES } from "../../../../constants/modal";
import PhotoCameraIcon from "../../../../assets/icons/PhotoCameraIcon";
import SettingsIcon from "../../../../assets/icons/SettingsIcon";

import "./index.css";

interface IPropsScanCTA {
  nextStep: () => void;
}

const ScanCTA: FC<IPropsScanCTA> = ({ nextStep }) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation("components/belly");

  const params = new URLSearchParams(window.location.search);
  const lang = params.get("lang");
  const market = params.get("market");

  const langBase = getLangBase(market || lang || "");
  const langRules =
    LANGUAGE_RULES[langBase as keyof typeof LANGUAGE_RULES] ||
    LANGUAGE_RULES.default;

  const brandDefined = findBrandByDomain();

  const isBlackstore = brandDefined?.name === "Blackstore";
  const isKenzo = brandDefined?.name === "Kenzo";
  const isLacoste = brandDefined?.name === "Lacoste";
  const isPeserico = brandDefined?.name === "Peserico";
  const isTDF = brandDefined?.name === "TDF";
  const isGivenchy = brandDefined?.name === "isGivenchy";

  const isBlackstoreMobile = isBlackstore && isMobile;

  const scanTextTransform =
    scanCTAStyle.textTransform as React.CSSProperties["textTransform"];

  const shouldHide =
    !scanCTAStyle ||
    isEmptyCTAStyle(scanCTAStyle) ||
    (isKenzo && langRules.unit === "imperial");

  if (shouldHide || uxRouteQuestionOnly) return null;

  const CTAStyles: React.CSSProperties = {
    display: "flex",
    border: `1px solid ${scanCTAStyle.borderColor}`,
    borderWidth: isLacoste ? "1px 0 1px 0" : scanCTAStyle.borderWidth,
    borderRadius: scanCTAStyle.borderRadius,
    color: scanCTAStyle.fontColor,
    fontWeight: scanCTAStyle.fontWeight,
    textTransform:
      scanTextTransform === "capitalize" ? "none" : scanTextTransform,
    marginTop: isMobile ? "10px" : "0",
    width: "calc(100% - 3px)",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "48px",
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    cursor: "pointer",
    letterSpacing: scanCTAStyle.letterSpacing,
    padding: isTDF ? "0" : isMobile ? "11px 0" : "12px 0",
    marginBottom: isTDF && isMobile ? "20px" : "",
  };

  const contentStyle: React.CSSProperties = {
    display: "flex",
    gap: isTDF ? 0 : isBlackstoreMobile ? "5px" : "10px",
    textDecoration:
      scanCTAStyle.fontStyle === "underline" ? "none" : scanCTAStyle.fontStyle,
    alignItems: "center",
    width: "100%",
    justifyContent: "center",
    padding: isPeserico
      ? "12px 16px"
      : !isMobile
        ? 0
        : isBlackstoreMobile
          ? "0 5px"
          : "0 24px",
    whiteSpace: isBlackstoreMobile ? "nowrap" : "normal",
  };

  const iconWrapperStyle: React.CSSProperties = {
    minWidth: isBlackstoreMobile ? "0px" : "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const iconStyle: React.CSSProperties = {
    width: isBlackstoreMobile || isTDF ? "13px" : "19px",
    height: isBlackstoreMobile || isTDF ? "10px" : "16px",
  };

  const textStyle: React.CSSProperties = {
    fontSize: isBlackstoreMobile
      ? `calc(${scanCTAStyle.fontSize} - 4px)`
      : isMobile && !isBlackstore
        ? `calc(${scanCTAStyle.fontSize} - 1px)`
        : scanCTAStyle.fontSize,
    letterSpacing: scanCTAStyle.letterSpacing,
    wordBreak: isBlackstoreMobile ? "normal" : "break-word",
  };

  const text =
    scanTextTransform === "capitalize"
      ? capitalizeFirstLetter(t("scan"))
      : t("scan");

  return (
    <div
      style={CTAStyles}
      className="scan-CTA-button"
      role="button"
      tabIndex={0}
      onClick={() => nextStep()}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          nextStep();
        }
      }}
    >
      <div style={contentStyle}>
        <div style={iconWrapperStyle}>
          {isTDF ? (
            <SettingsIcon style={iconStyle} fill={scanCTAStyle.fontColor} />
          ) : (
            <PhotoCameraIcon style={iconStyle} fill={scanCTAStyle.fontColor} />
          )}
        </div>

        <span
          style={textStyle}
          className={
            scanCTAStyle.fontStyle === "underline" ? "scan-text underline" : ""
          }
        >
          {text}
        </span>
      </div>
    </div>
  );
};

export default ScanCTA;
